import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { Task } from "model/activitiesModel";
import { UserData } from "hooks/useUserDataKeys";
import { getTranslation } from "common";
import { RichTextEditor } from "components/Editor/plugins/RichTextEditor";
import FieldsList from "components/FieldsList";
import UserDataFieldsInfo from "components/UserDataFieldsInfo";
import AttachmentList from "components/AttachmentList";
import DropZone from "components/DropZone";

interface MobileDetailContentProps {
  task: Task;
  descriptionDetail: string | null;
  descriptionDetailReadOnly: boolean;
  descriptionDetailRef: React.RefObject<import("@mdxeditor/editor").MDXEditorMethods>;
  isDescriptionDetailFocused: boolean;
  setIsDescriptionDetailFocused: (v: boolean) => void;
  saveDescriptionDetail: () => void;
  setShowEditClosedConfirm: (v: boolean) => void;
  userdataEntries: { [key: string]: UserData };
  setUserDataEntries: React.Dispatch<React.SetStateAction<{ [key: string]: UserData }>>;
  /**
   * Optional. The AddField dialog is owned by a ghost FieldsList
   * mounted at the drawer level so it can be opened from any tab;
   * the inline list inside `MobileDetailContent` therefore doesn't
   * need this prop anymore but it remains accepted (and forwarded
   * to FieldsList) for backward compatibility with any caller that
   * still passes it.
   */
  openAddFieldDialog?: boolean;
  setOpenAddFieldDialog?: (v: boolean) => void;
  attachments: any[];
  images: { [id: number]: string };
  histories: { [id: number]: any[] };
  getAttachments: () => Promise<void>;
  handleUploadFile: (files: File[]) => Promise<void>;
  doing: boolean;
  readonly: boolean;
  refresh?: () => void;
  sendSnack: (payload: { type?: "success" | "error" | "info" | "warning"; message: string }) => void;
  translations: Record<string, string>;
}

/**
 * Mobile "Scheda & Campi" tab content.
 * Renders: process title (with subtitle), description, RichTextEditor (only if present),
 * fields accordion, attachments accordion. Compact padding and grouped cards.
 */
const MobileDetailContent: React.FC<MobileDetailContentProps> = ({
  task,
  descriptionDetail,
  descriptionDetailReadOnly,
  descriptionDetailRef,
  isDescriptionDetailFocused,
  setIsDescriptionDetailFocused,
  saveDescriptionDetail,
  setShowEditClosedConfirm,
  userdataEntries,
  setUserDataEntries,
  openAddFieldDialog,
  setOpenAddFieldDialog,
  attachments,
  images,
  histories,
  getAttachments,
  handleUploadFile,
  doing,
  readonly,
  translations,
}) => {
  const [fieldsOpen, setFieldsOpen] = useState(true);
  const [attachmentsOpen, setAttachmentsOpen] = useState(true);

  const showDescriptionEditor = descriptionDetail || !descriptionDetailReadOnly;

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        p: 1,
        bgcolor: (t) => (t.palette.mode === "dark" ? "background.default" : "grey.50"),
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
      }}
    >
      <Stack spacing={1}>
        {/* Process name (description) — single card. The
            processmodelname and flownodename are already shown in the
            header, so the only piece worth showing again in the body
            is the "process name" (description), which we surface with
            a subtle bold inside a soft-background box. */}
        {task.description && (
          <Box
            sx={{
              p: 1.25,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              bgcolor: "background.paper",
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontSize: 9,
                fontWeight: 800,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                mb: 0.5,
              }}
            >
              {getTranslation(translations, "pm.processstart.processname.label")}
            </Typography>
            <Box
              sx={{
                p: 1,
                bgcolor: (t) => (t.palette.mode === "dark" ? "background.default" : "grey.50"),
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: 12, color: "text.primary", fontWeight: 600 }}
              >
                {task.description}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Rich text description (only render when there's something to show or user can edit) */}
        {showDescriptionEditor && (
          <Box
            sx={{
              p: 1.25,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              bgcolor: "background.paper",
            }}
            onMouseDownCapture={(e) => {
              if (descriptionDetailReadOnly) return;
              void e;
            }}
            onFocusCapture={() => {
              if (!descriptionDetailReadOnly) return;
              setShowEditClosedConfirm(false);
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                fontSize: 9,
                fontWeight: 800,
                color: "text.secondary",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                mb: 0.75,
              }}
            >
              {getTranslation(translations, "pm.startprocess.description")}
            </Typography>
            <RichTextEditor
              ref={descriptionDetailRef}
              value={descriptionDetail || ""}
              readOnly={descriptionDetailReadOnly}
              minHeight={isDescriptionDetailFocused ? 180 : 80}
              onFocus={() => setIsDescriptionDetailFocused(true)}
              onBlur={() => {
                setIsDescriptionDetailFocused(false);
                saveDescriptionDetail();
              }}
              sx={{
                width: "100%",
                "& [class*='_root_']": { fontSize: "0.875rem" },
                "& [contenteditable]": { fontSize: "0.875rem" },
                "& [contenteditable] h1": { fontSize: "1.3rem" },
                "& [contenteditable] h2": { fontSize: "1.15rem" },
                "& [contenteditable] h3": { fontSize: "1rem" },
                "& [contenteditable] table": { fontSize: "0.875rem" },
                "& .cm-editor": { fontSize: "0.875rem" },
              }}
            />
          </Box>
        )}

        {/* Fields accordion */}
        <Box
          sx={{
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1.5,
            overflow: "hidden",
          }}
        >
          <Accordion
            expanded={fieldsOpen}
            onChange={(_, isExp) => setFieldsOpen(isExp)}
            elevation={0}
            disableGutters
            sx={{
              "&:before": { display: "none" },
              boxShadow: "none",
              "&.Mui-expanded": { margin: 0 },
            }}
          >
            <AccordionSummary expandIcon={<ArrowDropDown />}>
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  {getTranslation(translations, "generic.user_data.caption")}
                </Typography>
                <UserDataFieldsInfo stopPropagation />
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 1 }}>
              <FieldsList
                canManage={doing && !readonly}
                schema={task.userschema}
                selectedProcess={task.idprocess}
                value={userdataEntries}
                onChange={setUserDataEntries}
                // The AddField dialog is owned by the ghost FieldsList
                // at the drawer level so it works from any tab. The
                // inline FieldsList below does NOT open the dialog
                // itself — its `openAddFieldDialog` is left undefined
                // so the useEffect that would call setOpen(true) is a
                // no-op, avoiding a race with the ghost.
                handleSaveUserdata={async (): Promise<void> => {
                  // Persistence is delegated to FieldsList's own save handler via its prop.
                  // This is a no-op stub because FieldsList handles saving internally.
                }}
                readonly={task.state === 0}
              />
            </AccordionDetails>
          </Accordion>
        </Box>

        {/* Attachments accordion */}
        {(attachments.length > 0 || (doing && !readonly)) && (
          <Box
            sx={{
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1.5,
              overflow: "hidden",
            }}
          >
            <Accordion
              expanded={attachmentsOpen}
              onChange={(_, isExp) => setAttachmentsOpen(isExp)}
              elevation={0}
              disableGutters
              sx={{
                "&:before": { display: "none" },
                boxShadow: "none",
                "&.Mui-expanded": { margin: 0 },
              }}
            >
              <AccordionSummary expandIcon={<ArrowDropDown />}>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}
                >
                  {getTranslation(translations, "settings.attachments.label")} ({attachments.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 1 }}>
                {doing && !readonly && (
                  <DropZone
                    files={[]}
                    externalFilesCount={attachments.length}
                    maxFiles={10 - attachments.length}
                    onFileChange={async (files) => {
                      if (files.length > 0) await handleUploadFile(files);
                    }}
                  />
                )}
                <AttachmentList
                  canManage={doing && !readonly}
                  attachments={attachments}
                  knownWidth={0}
                  getAttachments={getAttachments}
                  images={images}
                  histories={histories}
                />
              </AccordionDetails>
            </Accordion>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default MobileDetailContent;